// SPDX-License-Identifier: AGPL-3.0-or-later

import {ClientInfo} from '@app/features/app/components/dialogs/components/ClientInfo';
import {LogoutModal} from '@app/features/app/components/dialogs/components/LogoutModal';
import styles from '@app/features/app/components/dialogs/components/MobileSettingsView.module.css';
import {SettingsSearch} from '@app/features/app/components/dialogs/components/SettingsSearch';
import {
	MobileHeader,
	MobileHeaderWithBanner,
	MobileSettingsDangerItem,
} from '@app/features/app/components/dialogs/shared/MobileSettingsComponents';
import {SettingsCurrentTabProvider} from '@app/features/app/components/dialogs/shared/SettingsCurrentTabContext';
import {LongPressable} from '@app/features/app/components/LongPressable';
import {MobileRailTabIcon} from '@app/features/app/components/layout/mobile_rail/MobileRailTabLabels';
import {visibleMobileRailTabs} from '@app/features/app/components/layout/mobile_rail/MobileRailTypes';
import {isAgentsEnabled} from '@app/features/app/components/layout/sidebar_nav/SidebarPagesLive';
import {hasWhatsNewEntries} from '@app/features/app/components/whats_new/WhatsNewEntries';
import {openWhatsNewModal} from '@app/features/app/components/whats_new/WhatsNewModal';
import {getStatusTypeLabel} from '@app/features/app/constants/AppConstants';
import {usePressable} from '@app/features/app/hooks/usePressable';
import AuthSessionState from '@app/features/auth/state/AuthSession';
import UserConnection from '@app/features/connection/state/UserConnection';
import {SETTINGS_DESCRIPTOR, SIGN_OUT_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {isKeyboardActivationKey} from '@app/features/input/utils/KeyboardUtils';
import KonvoCalendarPrefs from '@app/features/konvo_calendar/state/KonvoCalendarPrefs';
import {HOME_LAYOUT_LABEL} from '@app/features/konvo_today/components/settings/CustomiseHomeMeta';
import HomePreferences from '@app/features/konvo_today/state/HomePreferences';
import {activateLatestServiceWorker} from '@app/features/platform/types/Versioning';
import {Logger} from '@app/features/platform/utils/AppLogger';
import Presence from '@app/features/presence/state/Presence';
import Theme from '@app/features/theme/state/Theme';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import {Scroller, type ScrollerHandle} from '@app/features/ui/components/Scroller';
import {Spinner} from '@app/features/ui/components/Spinner';
import {StatusAwareAvatar} from '@app/features/ui/components/StatusAwareAvatar';
import {StatusIndicator} from '@app/features/ui/components/StatusIndicator';
import {
	ArrowClockwiseIcon,
	CaretDownIcon,
	CaretRightIcon,
	type IconWeight,
	MegaphoneIcon,
	PencilSimpleIcon,
	SignOutIcon,
	SparkleIcon,
} from '@app/features/ui/icons/Icons';
import {StatusChangeBottomSheet} from '@app/features/user/components/modals/StatusChangeBottomSheet';
import ApplicationsTabState from '@app/features/user/components/modals/tabs/applications_tab/ApplicationsTabState';
import PhoneNotificationsPage, {
	PHONE_NOTIFICATIONS_SUBPAGE_TITLES,
} from '@app/features/user/components/modals/tabs/notifications_tab/PhoneNotificationsPage';
import userSettingsStyles from '@app/features/user/components/modals/UserSettingsModal.module.css';
import {getSettingsTabComponent} from '@app/features/user/components/settings_utils/DesktopSettingsTabs';
import type {SettingsTab} from '@app/features/user/components/settings_utils/SettingsConstants';
import {
	getCategoryLabel,
	getUserSettingsTabLabel,
} from '@app/features/user/components/settings_utils/SettingsConstants';
import {isSettingsItemNew} from '@app/features/user/components/settings_utils/SettingsMetadata';
import {
	ACCOUNT_SETTINGS_TAB,
	getAccountSectionForLegacySection,
	getAccountSectionForNestedTab,
	PRIMARY_SETTINGS_NAV_HIDDEN_TAB_TYPES,
	PROFILE_SETTINGS_TAB,
} from '@app/features/user/components/settings_utils/SettingsNavigationGroups';
import {
	getAdvancedSettingItems,
	searchSettings,
} from '@app/features/user/components/settings_utils/SettingsSearchIndex';
import type {
	SearchableSettingItem,
	UserSettingsTabType,
} from '@app/features/user/components/settings_utils/SettingsSectionRegistry';
import {SettingsStatusBadge} from '@app/features/user/components/settings_utils/SettingsStatusBadge';
import {buildUserSettingsDeepLink} from '@app/features/user/components/settings_utils/UserSettingsDeepLinks';
import type {MobileNavigationState} from '@app/features/user/hooks/useMobileNavigation';
import {useSettingsContentKey} from '@app/features/user/hooks/useSettingsContentKey';
import {useUnsavedChangesFlash} from '@app/features/user/hooks/useUnsavedChangesFlash';
import Users from '@app/features/user/state/Users';
import {ThemeTypes} from '@fluxer/constants/src/UserConstants';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {AnimatePresence, motion} from 'framer-motion';
import {observer} from 'mobx-react-lite';
import React, {type UIEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react';

const UPDATE_SERVICE_WORKER_DESCRIPTOR = msg({
	message: 'Update service worker',
	comment: 'Short label in the settings dialog mobile settings view.',
});
const WHAT_S_NEW_DESCRIPTOR = msg({
	message: "What's new",
	comment: 'Short label in the settings dialog mobile settings view.',
});
const DEBUG_DESCRIPTOR = msg({
	message: 'Debug',
	comment: 'Short label in the settings dialog mobile settings view.',
});
const APPLICATION_DETAILS_DESCRIPTOR = msg({
	message: 'Application details',
	comment: 'Settings content title shown while a developer application detail page is loading.',
});
const EDIT_PROFILE_DESCRIPTOR = msg({
	message: 'Edit profile',
	comment: 'Pill under the avatar on the mobile settings list that opens the profile tab.',
});
const CHANGE_STATUS_DESCRIPTOR = msg({
	message: 'Change status',
	comment: 'Accessible label for the status pill on the mobile settings list.',
});
const N_LINKED_DESCRIPTOR = msg({
	message: '{count, plural, one {# linked} other {# linked}}',
	comment: 'Value on the Connections row of the mobile settings list: how many accounts are linked.',
});
const DESKTOP_VALUE_DESCRIPTOR = msg({
	message: 'Desktop',
	comment: 'Value on the Rail row of the mobile settings list: that setting applies to the desktop app.',
});
const THEME_GLASS_DESCRIPTOR = msg({
	message: 'Glass',
	comment: 'Theme name shown on the Appearance row of the mobile settings list.',
});
const THEME_GLASS_LIGHT_DESCRIPTOR = msg({
	message: 'Light glass',
	comment: 'Theme name shown on the Appearance row of the mobile settings list.',
});
const THEME_LIGHT_DESCRIPTOR = msg({
	message: 'Light',
	comment: 'Theme name shown on the Appearance row of the mobile settings list.',
});
const THEME_DARK_DESCRIPTOR = msg({
	message: 'Dark',
	comment: 'Theme name shown on the Appearance row of the mobile settings list.',
});
const THEME_SYSTEM_DESCRIPTOR = msg({
	message: 'System',
	comment: 'Theme name shown on the Appearance row of the mobile settings list.',
});
const SEARCH_PAGES_DESCRIPTOR = msg({
	message: 'Pages',
	comment: 'Heading above settings pages that match a search on the mobile settings list.',
});
const SEARCH_SETTINGS_RESULTS_DESCRIPTOR = msg({
	message: 'Settings',
	comment: 'Heading above individual settings that match a search on the mobile settings list.',
});
const logger = new Logger('MobileSettingsView');
const LONG_PRESS_CLICK_SUPPRESS_MS = 750;

type SettingsPageLongPressEvent = React.PointerEvent<HTMLElement> | React.TouchEvent<HTMLElement>;

function useSettingsPageDeepLinkLongPress(tabType: UserSettingsTabType): {
	handleLongPressCopy: (event: SettingsPageLongPressEvent) => void;
	consumeSuppressedClick: (event: React.MouseEvent<HTMLElement>) => boolean;
} {
	const {i18n} = useLingui();
	const suppressNextClickRef = useRef(false);
	const suppressClickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const clearSuppressedClick = useCallback(() => {
		suppressNextClickRef.current = false;
		if (suppressClickTimeoutRef.current) {
			clearTimeout(suppressClickTimeoutRef.current);
			suppressClickTimeoutRef.current = null;
		}
	}, []);
	const suppressNextClick = useCallback(() => {
		suppressNextClickRef.current = true;
		if (suppressClickTimeoutRef.current) {
			clearTimeout(suppressClickTimeoutRef.current);
		}
		suppressClickTimeoutRef.current = setTimeout(() => {
			suppressNextClickRef.current = false;
			suppressClickTimeoutRef.current = null;
		}, LONG_PRESS_CLICK_SUPPRESS_MS);
	}, []);
	useEffect(() => clearSuppressedClick, [clearSuppressedClick]);
	const handleLongPressCopy = useCallback(
		(event: SettingsPageLongPressEvent) => {
			event.preventDefault();
			event.stopPropagation();
			suppressNextClick();
			void TextCopyCommands.copy(i18n, buildUserSettingsDeepLink(tabType));
		},
		[i18n, suppressNextClick, tabType],
	);
	const consumeSuppressedClick = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			if (!suppressNextClickRef.current) return false;
			event.preventDefault();
			event.stopPropagation();
			clearSuppressedClick();
			return true;
		},
		[clearSuppressedClick],
	);
	return {handleLongPressCopy, consumeSuppressedClick};
}

interface MobileSettingsViewProps {
	groupedSettingsTabs: Record<string, Array<SettingsTab>>;
	currentTab: SettingsTab | undefined;
	mobileNav: MobileNavigationState;
	onBack: () => void;
	onTabSelect: (tab: string, title: string) => void;
	initialGuildId?: string;
	initialSubtab?: string;
	pendingSection?: string | null;
	onPendingSectionConsumed?: () => void;
}

interface PressableSettingsItemProps {
	tab: SettingsTab;
	onSelect: () => void;
	badge?: React.ReactNode;
	/** Small trailing value (current theme, "3 linked", the rail miniature). */
	value?: React.ReactNode;
}

const PressableSettingsItem: React.FC<PressableSettingsItemProps> = observer(({tab, onSelect, badge, value}) => {
	const {isPressed, pressableProps} = usePressable();
	const {handleLongPressCopy, consumeSuppressedClick} = useSettingsPageDeepLinkLongPress(tab.type);
	const handleClick = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			if (consumeSuppressedClick(event)) return;
			onSelect();
		},
		[consumeSuppressedClick, onSelect],
	);
	const IconComponent = tab.icon;
	return (
		<LongPressable
			className={clsx(styles.settingsItem, isPressed && styles.settingsItemPressed)}
			role="button"
			tabIndex={0}
			onClick={handleClick}
			onLongPress={handleLongPressCopy}
			onKeyDown={(event) => {
				if (!isKeyboardActivationKey(event.key)) return;
				event.preventDefault();
				onSelect();
			}}
			data-flx="app.mobile-settings-view.pressable-settings-item.settings-item.select"
			{...pressableProps}
		>
			<span
				className={styles.settingsItemTile}
				data-flx="app.mobile-settings-view.pressable-settings-item.settings-item-tile"
			>
				<IconComponent
					className={styles.settingsItemIcon}
					weight={tab.iconWeight ?? 'fill'}
					data-flx="app.mobile-settings-view.pressable-settings-item.settings-item-icon"
				/>
			</span>
			<div
				className={styles.settingsItemContent}
				data-flx="app.mobile-settings-view.pressable-settings-item.settings-item-content"
			>
				<div
					className={styles.settingsItemLabelContainer}
					data-flx="app.mobile-settings-view.pressable-settings-item.settings-item-label-container"
				>
					<span
						className={styles.settingsItemLabel}
						data-flx="app.mobile-settings-view.pressable-settings-item.settings-item-label"
					>
						{tab.label}
					</span>
					{badge}
				</div>
			</div>
			{value !== undefined && value !== null && (
				<span
					className={styles.settingsItemValue}
					data-flx="app.mobile-settings-view.pressable-settings-item.settings-item-value"
				>
					{value}
				</span>
			)}
			<CaretRightIcon
				className={styles.settingsItemArrow}
				data-flx="app.mobile-settings-view.pressable-settings-item.settings-item-arrow"
			/>
		</LongPressable>
	);
});

/** The Mobile rail row's miniature: the chosen tab icons plus the dark Ask pill. */
const MobileRailMiniature = observer(() => {
	useEffect(() => {
		void KonvoCalendarPrefs.load();
	}, []);
	return (
		<span className={styles.railMini} aria-hidden="true" data-flx="app.mobile-settings-view.rail-miniature">
			{visibleMobileRailTabs(KonvoCalendarPrefs.mobileRailTabs, isAgentsEnabled()).map((tab) => (
				<MobileRailTabIcon
					key={tab}
					tab={tab}
					size={13}
					mono
					className={styles.railMiniIcon}
					data-flx="app.mobile-settings-view.rail-miniature.icon"
				/>
			))}
			<b className={styles.railMiniAsk} data-flx="app.mobile-settings-view.rail-miniature.ask">
				<SparkleIcon size={11} data-flx="app.mobile-settings-view.rail-miniature.ask-icon" />
			</b>
		</span>
	);
});

function themeValueDescriptor(theme: string) {
	switch (theme) {
		case ThemeTypes.GLASS:
			return THEME_GLASS_DESCRIPTOR;
		case ThemeTypes.GLASS_LIGHT:
			return THEME_GLASS_LIGHT_DESCRIPTOR;
		case ThemeTypes.LIGHT:
			return THEME_LIGHT_DESCRIPTOR;
		case ThemeTypes.DARK:
		case ThemeTypes.COAL:
		case ThemeTypes.DARK_LEGACY:
			return THEME_DARK_DESCRIPTOR;
		case ThemeTypes.SYSTEM:
			return THEME_SYSTEM_DESCRIPTOR;
		default:
			return null;
	}
}

/** Trailing value for a settings row, or null when there is nothing worth showing. */
const SettingsItemValue = observer(({tabType}: {tabType: UserSettingsTabType}) => {
	const {i18n} = useLingui();
	switch (tabType) {
		case 'appearance': {
			const descriptor = themeValueDescriptor(Theme.themePreference);
			return descriptor ? i18n._(descriptor) : null;
		}
		case 'linked_accounts': {
			const count = UserConnection.connections.size;
			return count > 0 ? i18n._(N_LINKED_DESCRIPTOR, {count}) : null;
		}
		case 'konvo_home':
			return i18n._(HOME_LAYOUT_LABEL[HomePreferences.effectiveLayout]);
		case 'mobile_rail':
			return <MobileRailMiniature data-flx="app.mobile-settings-view.settings-item-value.rail-miniature" />;
		case 'konvo_rail':
			return i18n._(DESKTOP_VALUE_DESCRIPTOR);
		case 'devices': {
			const count = AuthSessionState.authSessions.length;
			return AuthSessionState.fetchStatus === 'success' && count > 0 ? String(count) : null;
		}
		default:
			return null;
	}
});

interface MobileSettingsActionItemProps {
	icon: React.ComponentType<{className?: string; weight?: IconWeight}>;
	label: React.ReactNode;
	onClick: () => void;
	isLoading?: boolean;
	iconWeight?: IconWeight;
	showArrow?: boolean;
}

const MobileSettingsActionItem: React.FC<MobileSettingsActionItemProps> = observer(
	({icon: IconComponent, label, onClick, isLoading = false, iconWeight, showArrow = true}) => {
		const {isPressed, pressableProps} = usePressable();
		return (
			<LongPressable
				className={clsx(styles.settingsItem, isPressed && styles.settingsItemPressed)}
				role="button"
				tabIndex={0}
				onClick={onClick}
				onKeyDown={(event) => {
					if (!isKeyboardActivationKey(event.key)) return;
					event.preventDefault();
					onClick();
				}}
				data-flx="app.mobile-settings-view.mobile-settings-action-item.settings-item.click"
				{...pressableProps}
			>
				<span
					className={styles.settingsItemTile}
					data-flx="app.mobile-settings-view.mobile-settings-action-item.settings-item-tile"
				>
					<IconComponent
						className={styles.settingsItemIcon}
						weight={iconWeight ?? 'regular'}
						data-flx="app.mobile-settings-view.mobile-settings-action-item.settings-item-icon"
					/>
				</span>
				<div
					className={styles.settingsItemContent}
					data-flx="app.mobile-settings-view.mobile-settings-action-item.settings-item-content"
				>
					<div
						className={styles.settingsItemLabelContainer}
						data-flx="app.mobile-settings-view.mobile-settings-action-item.settings-item-label-container"
					>
						<span
							className={styles.settingsItemLabel}
							data-flx="app.mobile-settings-view.mobile-settings-action-item.settings-item-label"
						>
							{label}
						</span>
						{isLoading && (
							<Spinner
								size="small"
								className={styles.settingsItemSpinner}
								data-flx="app.mobile-settings-view.mobile-settings-action-item.settings-item-spinner"
							/>
						)}
					</div>
				</div>
				{showArrow && (
					<CaretRightIcon
						className={styles.settingsItemArrow}
						data-flx="app.mobile-settings-view.mobile-settings-action-item.settings-item-arrow"
					/>
				)}
			</LongPressable>
		);
	},
);
const ServiceWorkerUpdateButton = observer(() => {
	const {i18n} = useLingui();
	const [isUpdating, setIsUpdating] = useState(false);
	const handleUpdateServiceWorker = async () => {
		if (isUpdating) return;
		setIsUpdating(true);
		try {
			await activateLatestServiceWorker();
			window.location.reload();
		} catch (error) {
			logger.error('Failed to update service worker:', error);
		} finally {
			setIsUpdating(false);
		}
	};
	return (
		<MobileSettingsActionItem
			icon={ArrowClockwiseIcon}
			iconWeight="bold"
			label={i18n._(UPDATE_SERVICE_WORKER_DESCRIPTOR)}
			onClick={handleUpdateServiceWorker}
			isLoading={isUpdating}
			showArrow={false}
			data-flx="app.mobile-settings-view.service-worker-update-button.mobile-settings-action-item.update-service-worker"
		/>
	);
});
const MOBILE_HIDDEN_TAB_TYPES = new Set<UserSettingsTabType>(['keybinds', ...PRIMARY_SETTINGS_NAV_HIDDEN_TAB_TYPES]);

interface MobileProfileHeaderProps {
	onEditProfile: () => void;
}

/** Centred profile block at the top of the list: avatar, name, email, status pill, edit pill. */
const MobileProfileHeader: React.FC<MobileProfileHeaderProps> = observer(({onEditProfile}) => {
	const {i18n} = useLingui();
	const {handleLongPressCopy, consumeSuppressedClick} = useSettingsPageDeepLinkLongPress(PROFILE_SETTINGS_TAB);
	const [statusSheetOpen, setStatusSheetOpen] = useState(false);
	const handleEditClick = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			if (consumeSuppressedClick(event)) return;
			onEditProfile();
		},
		[consumeSuppressedClick, onEditProfile],
	);
	const currentUser = Users.currentUser;
	if (!currentUser) return null;
	const status = Presence.getStatus(currentUser.id);
	const secondary = currentUser.email || `@${currentUser.username}`;
	return (
		<div className={styles.profileHeader} data-flx="app.mobile-settings-view.mobile-profile-header">
			<StatusAwareAvatar
				size={76}
				user={currentUser}
				data-flx="app.mobile-settings-view.mobile-profile-header.status-aware-avatar"
			/>
			<span className={styles.profileName} data-flx="app.mobile-settings-view.mobile-profile-header.name">
				{currentUser.displayName}
			</span>
			<span className={styles.profileSecondary} data-flx="app.mobile-settings-view.mobile-profile-header.secondary">
				{secondary}
			</span>
			<div className={styles.profilePills} data-flx="app.mobile-settings-view.mobile-profile-header.pills">
				<button
					type="button"
					className={styles.profilePill}
					onClick={() => setStatusSheetOpen(true)}
					aria-label={i18n._(CHANGE_STATUS_DESCRIPTOR)}
					data-flx="app.mobile-settings-view.mobile-profile-header.status-pill"
				>
					<StatusIndicator
						status={status}
						size={8}
						data-flx="app.mobile-settings-view.mobile-profile-header.status-indicator"
					/>
					{Presence.getCustomStatus(currentUser.id)?.text ?? getStatusTypeLabel(i18n, status)}
					<CaretDownIcon
						className={styles.profilePillCaret}
						data-flx="app.mobile-settings-view.mobile-profile-header.status-caret"
					/>
				</button>
				<LongPressable
					className={styles.profilePill}
					role="button"
					tabIndex={0}
					onClick={handleEditClick}
					onLongPress={handleLongPressCopy}
					onKeyDown={(event) => {
						if (!isKeyboardActivationKey(event.key)) return;
						event.preventDefault();
						onEditProfile();
					}}
					data-flx="app.mobile-settings-view.mobile-profile-header.edit-pill"
				>
					<PencilSimpleIcon
						className={styles.profilePillIcon}
						data-flx="app.mobile-settings-view.mobile-profile-header.edit-icon"
					/>
					{i18n._(EDIT_PROFILE_DESCRIPTOR)}
				</LongPressable>
			</div>
			{statusSheetOpen && (
				<StatusChangeBottomSheet
					isOpen={statusSheetOpen}
					onClose={() => setStatusSheetOpen(false)}
					data-flx="app.mobile-settings-view.mobile-profile-header.status-sheet"
				/>
			)}
		</div>
	);
});
/** Where a search hit should land: the tab to open and the section to scroll to. */
function getSearchResultSection(tab: SettingsTab, item: SearchableSettingItem): string | null {
	if (tab.type === ACCOUNT_SETTINGS_TAB) {
		return (
			getAccountSectionForNestedTab(item.sourceTabType ?? item.tabType) ??
			getAccountSectionForLegacySection(item.sourceSectionId ?? item.sectionId) ??
			item.sectionId ??
			null
		);
	}
	return item.sectionId ?? null;
}

interface SearchResultRowProps {
	tab: SettingsTab;
	item: SearchableSettingItem;
	onSelect: () => void;
}

const SearchResultRow: React.FC<SearchResultRowProps> = observer(({tab, item, onSelect}) => {
	const {i18n} = useLingui();
	const {isPressed, pressableProps} = usePressable();
	const IconComponent = tab.icon;
	const sourceTabType = item.sourceTabType ?? item.tabType;
	const location =
		sourceTabType !== tab.type ? `${tab.label} · ${getUserSettingsTabLabel(i18n, sourceTabType)}` : tab.label;
	return (
		<LongPressable
			className={clsx(styles.settingsItem, isPressed && styles.settingsItemPressed)}
			role="button"
			tabIndex={0}
			onClick={onSelect}
			onKeyDown={(event) => {
				if (!isKeyboardActivationKey(event.key)) return;
				event.preventDefault();
				onSelect();
			}}
			data-flx="app.mobile-settings-view.search-result-row.select"
			{...pressableProps}
		>
			<span className={styles.settingsItemTile} data-flx="app.mobile-settings-view.search-result-row.tile">
				<IconComponent
					className={styles.settingsItemIcon}
					weight={tab.iconWeight ?? 'fill'}
					data-flx="app.mobile-settings-view.search-result-row.icon"
				/>
			</span>
			<div className={styles.settingsItemContent} data-flx="app.mobile-settings-view.search-result-row.content">
				<span className={styles.settingsItemLabel} data-flx="app.mobile-settings-view.search-result-row.label">
					{item.label}
				</span>
				<span className={styles.settingsItemSubtitle} data-flx="app.mobile-settings-view.search-result-row.location">
					{location}
				</span>
			</div>
			<CaretRightIcon
				className={styles.settingsItemArrow}
				data-flx="app.mobile-settings-view.search-result-row.arrow"
			/>
		</LongPressable>
	);
});

interface MobileSettingsSearchResultsProps {
	query: string;
	/** Every tab, including the ones the phone list hides (profile, account sub-pages). */
	groupedTabs: Record<string, Array<SettingsTab>>;
	visibleTabs: Record<string, Array<SettingsTab>>;
	onTabSelect: (tab: string, title: string) => void;
	onResultSelect: (tab: SettingsTab, section: string | null) => void;
}

/** Search mode of the phone settings list: matching pages first, then individual settings. */
const MobileSettingsSearchResults: React.FC<MobileSettingsSearchResultsProps> = observer(
	({query, groupedTabs, visibleTabs, onTabSelect, onResultSelect}) => {
		const {i18n} = useLingui();
		const {pages, settings} = useMemo(() => {
			const words = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
			const pages = Object.values(visibleTabs)
				.flat()
				.filter((tab) => {
					const label = tab.label.toLowerCase();
					return words.every((word) => label.includes(word));
				});
			// Hidden account sub-pages still resolve to Account; keybinds have no phone page.
			const searchableTabs = Object.values(groupedTabs)
				.flat()
				.filter((tab) => tab.type !== 'keybinds');
			const seen = new Set<string>();
			const settings: Array<{key: string; tab: SettingsTab; item: SearchableSettingItem}> = [];
			for (const result of searchSettings(query, searchableTabs)) {
				for (const item of result.matchedItems) {
					const key = `${result.tab.type}:${item.label.toLowerCase()}`;
					if (seen.has(key)) continue;
					seen.add(key);
					settings.push({key, tab: result.tab, item});
				}
			}
			return {pages, settings};
		}, [query, groupedTabs, visibleTabs]);
		if (pages.length === 0 && settings.length === 0) {
			return (
				<div className={styles.searchEmpty} data-flx="app.mobile-settings-view.search-results.empty">
					<Trans>No settings found</Trans>
				</div>
			);
		}
		return (
			<>
				{pages.length > 0 && (
					<div className={styles.categorySection} data-flx="app.mobile-settings-view.search-results.pages">
						<h2 className={styles.categoryTitle} data-flx="app.mobile-settings-view.search-results.pages-title">
							{i18n._(SEARCH_PAGES_DESCRIPTOR)}
						</h2>
						<div className={styles.categoryList} data-flx="app.mobile-settings-view.search-results.pages-list">
							{pages.map((tab, index) => (
								<div key={tab.type} data-flx="app.mobile-settings-view.search-results.page">
									<PressableSettingsItem
										tab={tab}
										onSelect={() => onTabSelect(tab.type, tab.label)}
										value={
											<SettingsItemValue
												tabType={tab.type}
												data-flx="app.mobile-settings-view.search-results.page-value"
											/>
										}
										data-flx="app.mobile-settings-view.search-results.pressable-settings-item.tab-select"
									/>
									{index < pages.length - 1 && (
										<div className={styles.divider} data-flx="app.mobile-settings-view.search-results.page-divider" />
									)}
								</div>
							))}
						</div>
					</div>
				)}
				{settings.length > 0 && (
					<div className={styles.categorySection} data-flx="app.mobile-settings-view.search-results.settings">
						<h2 className={styles.categoryTitle} data-flx="app.mobile-settings-view.search-results.settings-title">
							{i18n._(SEARCH_SETTINGS_RESULTS_DESCRIPTOR)}
						</h2>
						<div className={styles.categoryList} data-flx="app.mobile-settings-view.search-results.settings-list">
							{settings.map(({key, tab, item}, index) => (
								<div key={key} data-flx="app.mobile-settings-view.search-results.setting">
									<SearchResultRow
										tab={tab}
										item={item}
										onSelect={() => onResultSelect(tab, getSearchResultSection(tab, item))}
										data-flx="app.mobile-settings-view.search-results.search-result-row"
									/>
									{index < settings.length - 1 && (
										<div
											className={styles.divider}
											data-flx="app.mobile-settings-view.search-results.setting-divider"
										/>
									)}
								</div>
							))}
						</div>
					</div>
				)}
			</>
		);
	},
);

const MobileSettingsList = observer(
	({
		groupedTabs,
		onTabSelect,
		onSearchResultSelect,
		searchQuery,
		onSearchQueryChange,
		scrollRef,
		onScroll,
	}: {
		groupedTabs: Record<string, Array<SettingsTab>>;
		onTabSelect: (tab: string, title: string) => void;
		onSearchResultSelect: (tab: SettingsTab, section: string | null) => void;
		searchQuery: string;
		onSearchQueryChange: (value: string) => void;
		scrollRef?: React.Ref<ScrollerHandle>;
		onScroll?: (event: UIEvent<HTMLDivElement>) => void;
	}) => {
		const {i18n} = useLingui();
		const currentUser = Users.currentUser;
		const shouldShowWhatsNew = hasWhatsNewEntries();
		const advancedSettingsHasNew = useMemo(
			() =>
				getAdvancedSettingItems().some((item) => isSettingsItemNew(item, Date.now(), currentUser?.createdAt ?? null)),
			[currentUser?.createdAt],
		);
		const mobileVisibleTabs = useMemo(() => {
			const visibleTabs: Record<string, Array<SettingsTab>> = {};
			Object.entries(groupedTabs).forEach(([category, tabs]) => {
				const filteredCategoryTabs = tabs.filter((tab) => !MOBILE_HIDDEN_TAB_TYPES.has(tab.type));
				if (filteredCategoryTabs.length > 0) {
					visibleTabs[category] = filteredCategoryTabs;
				}
			});
			return visibleTabs;
		}, [groupedTabs]);
		const handleLogout = useCallback(() => {
			ModalCommands.push(modal(() => <LogoutModal data-flx="app.mobile-settings-view.handle-logout.logout-modal" />));
		}, []);
		const categories = Object.entries(mobileVisibleTabs);
		const lastCategoryIndex = categories.length - 1;
		const isSearching = searchQuery.trim().length > 0;
		return (
			<Scroller
				className={styles.scrollerContainer}
				key="mobile-settings-list-scroller"
				ref={scrollRef}
				onScroll={onScroll}
				data-flx="app.mobile-settings-view.mobile-settings-list.scroller-container"
			>
				<div className={styles.searchSection} data-flx="app.mobile-settings-view.mobile-settings-list.search-section">
					<SettingsSearch
						value={searchQuery}
						onChange={onSearchQueryChange}
						data-flx="app.mobile-settings-view.mobile-settings-list.settings-search"
					/>
				</div>
				{isSearching ? (
					<MobileSettingsSearchResults
						query={searchQuery}
						groupedTabs={groupedTabs}
						visibleTabs={mobileVisibleTabs}
						onTabSelect={onTabSelect}
						onResultSelect={onSearchResultSelect}
						data-flx="app.mobile-settings-view.mobile-settings-list.search-results"
					/>
				) : (
					<>
						<div
							className={styles.profileSection}
							data-flx="app.mobile-settings-view.mobile-settings-list.profile-section"
						>
							<MobileProfileHeader
								onEditProfile={() =>
									onTabSelect(PROFILE_SETTINGS_TAB, getUserSettingsTabLabel(i18n, PROFILE_SETTINGS_TAB))
								}
								data-flx="app.mobile-settings-view.mobile-settings-list.mobile-profile-header.profile-tab-select"
							/>
						</div>
						{categories.map(([category, tabs], categoryIndex) => (
							<div
								key={category}
								className={styles.categorySection}
								data-flx="app.mobile-settings-view.mobile-settings-list.category-section"
							>
								<h2
									className={styles.categoryTitle}
									data-flx="app.mobile-settings-view.mobile-settings-list.category-title"
								>
									{getCategoryLabel(category as SettingsTab['category'])}
								</h2>
								<div
									className={styles.categoryList}
									data-flx="app.mobile-settings-view.mobile-settings-list.category-list"
								>
									{tabs.map((tab, index) => {
										const isLastTab = index === tabs.length - 1;
										const isLastCategory = categoryIndex === lastCategoryIndex;
										const badge =
											tab.type === 'advanced_settings' && advancedSettingsHasNew ? (
												<SettingsStatusBadge
													kind="new"
													data-flx="app.mobile-settings-view.mobile-settings-list.settings-status-badge"
												/>
											) : undefined;
										return (
											<div key={tab.type} data-flx="app.mobile-settings-view.mobile-settings-list.div">
												<PressableSettingsItem
													tab={tab}
													onSelect={() => onTabSelect(tab.type, tab.label)}
													badge={badge}
													value={
														<SettingsItemValue
															tabType={tab.type}
															data-flx="app.mobile-settings-view.mobile-settings-list.settings-item-value"
														/>
													}
													data-flx="app.mobile-settings-view.mobile-settings-list.pressable-settings-item.tab-select"
												/>
												{(!isLastTab || (isLastCategory && shouldShowWhatsNew)) && (
													<div
														className={styles.divider}
														data-flx="app.mobile-settings-view.mobile-settings-list.divider"
													/>
												)}
											</div>
										);
									})}
									{categoryIndex === lastCategoryIndex && shouldShowWhatsNew && (
										<MobileSettingsActionItem
											icon={MegaphoneIcon}
											iconWeight="fill"
											label={i18n._(WHAT_S_NEW_DESCRIPTOR)}
											onClick={openWhatsNewModal}
											data-flx="app.mobile-settings-view.mobile-settings-list.mobile-settings-action-item.open-whats-new-modal"
										/>
									)}
								</div>
							</div>
						))}
						<div
							className={clsx(styles.categorySection, styles.logoutSection)}
							data-flx="app.mobile-settings-view.mobile-settings-list.logout-section"
						>
							<div
								className={clsx(styles.categoryList, styles.logoutCard)}
								data-flx="app.mobile-settings-view.mobile-settings-list.logout-card"
							>
								<MobileSettingsDangerItem
									icon={SignOutIcon}
									label={i18n._(SIGN_OUT_DESCRIPTOR)}
									onClick={handleLogout}
									data-flx="app.mobile-settings-view.mobile-settings-list.mobile-settings-danger-item.logout"
								/>
							</div>
						</div>
						<div
							className={styles.categorySection}
							data-flx="app.mobile-settings-view.mobile-settings-list.category-section--2"
						>
							<h2
								className={styles.categoryTitle}
								data-flx="app.mobile-settings-view.mobile-settings-list.category-title--2"
							>
								{i18n._(DEBUG_DESCRIPTOR)}
							</h2>
							<div
								className={styles.categoryList}
								data-flx="app.mobile-settings-view.mobile-settings-list.category-list--2"
							>
								<ServiceWorkerUpdateButton data-flx="app.mobile-settings-view.mobile-settings-list.service-worker-update-button" />
							</div>
						</div>
						<div
							className={styles.clientInfoContainer}
							data-flx="app.mobile-settings-view.mobile-settings-list.client-info-container"
						>
							<ClientInfo data-flx="app.mobile-settings-view.mobile-settings-list.client-info" />
						</div>
					</>
				)}
			</Scroller>
		);
	},
);
const contentFadeVariants = {
	enter: {opacity: 0},
	center: {opacity: 1},
	exit: {opacity: 0},
};
const headerFadeVariants = {
	enter: {opacity: 0},
	center: {opacity: 1},
	exit: {opacity: 0},
};

interface MobileContentWithScrollSpyProps {
	scrollKey: string;
	settingsTabType?: UserSettingsTabType;
	initialGuildId?: string;
	initialSubtab?: string;
	currentTabComponent: React.ComponentType<Record<string, unknown>> | null;
	pendingSection?: string | null;
	onPendingSectionConsumed?: () => void;
}

const MobileContentWithScrollSpy: React.FC<MobileContentWithScrollSpyProps> = observer(
	({
		scrollKey,
		settingsTabType,
		initialGuildId,
		initialSubtab,
		currentTabComponent,
		pendingSection,
		onPendingSectionConsumed,
	}) => {
		useEffect(() => {
			if (!pendingSection) return;
			const frame = window.requestAnimationFrame(() => {
				const element = document.getElementById(pendingSection);
				if (element) {
					element.scrollIntoView({behavior: 'auto', block: 'start'});
				}
				onPendingSectionConsumed?.();
			});
			return () => window.cancelAnimationFrame(frame);
		}, [pendingSection, onPendingSectionConsumed]);
		return (
			<Scroller
				className={styles.scrollerFlex}
				key={scrollKey}
				data-settings-scroll-container
				data-flx="app.mobile-settings-view.mobile-content-with-scroll-spy.scroller-flex"
			>
				<div
					className={styles.contentContainer}
					data-flx="app.mobile-settings-view.mobile-content-with-scroll-spy.content-container"
				>
					{currentTabComponent &&
						(settingsTabType ? (
							<SettingsCurrentTabProvider
								value={settingsTabType}
								data-flx="app.mobile-settings-view.mobile-content-with-scroll-spy.settings-current-tab-provider"
							>
								{React.createElement(currentTabComponent, {
									settingsTabType,
									...(initialGuildId ? {initialGuildId} : {}),
									...(initialSubtab ? {initialSubtab} : {}),
								} as Record<string, unknown>)}
							</SettingsCurrentTabProvider>
						) : (
							React.createElement(currentTabComponent, {
								...(initialGuildId ? {initialGuildId} : {}),
								...(initialSubtab ? {initialSubtab} : {}),
							} as Record<string, unknown>)
						))}
				</div>
			</Scroller>
		);
	},
);
export const MobileSettingsView: React.FC<MobileSettingsViewProps> = observer(
	({
		groupedSettingsTabs,
		currentTab,
		mobileNav,
		onBack,
		onTabSelect,
		initialGuildId,
		initialSubtab,
		pendingSection,
		onPendingSectionConsumed,
	}) => {
		const {i18n} = useLingui();
		const currentTabId = mobileNav.currentView?.tab;
		const {showUnsavedBanner, flashBanner, tabData, checkUnsavedChanges} = useUnsavedChangesFlash(currentTabId);
		const {contentKey} = useSettingsContentKey();
		const [initialPendingSection, setInitialPendingSection] = useState<string | null>(() => initialSubtab ?? null);
		useEffect(() => {
			setInitialPendingSection(initialSubtab ?? null);
		}, [initialSubtab]);
		const scrollKey = useMemo(() => {
			if (!currentTabId) {
				return 'user-settings-mobile-root';
			}
			const subtabKey = contentKey ?? initialSubtab ?? 'root';
			return `user-settings-${currentTabId}-${subtabKey}`;
		}, [contentKey, currentTabId, initialSubtab]);
		// The Notifications tab's own pages: leaving the tab, or Settings, starts at its list next time.
		useEffect(() => {
			if (currentTabId !== 'notifications') PhoneNotificationsPage.back();
		}, [currentTabId]);
		useEffect(() => () => PhoneNotificationsPage.back(), []);
		const handleBack = useCallback(() => {
			if (checkUnsavedChanges()) return;
			if (currentTabId === 'applications' && ApplicationsTabState.isDetailView) {
				void ApplicationsTabState.navigateToList();
				return;
			}
			if (currentTabId === 'notifications' && PhoneNotificationsPage.isSubpage) {
				PhoneNotificationsPage.back();
				return;
			}
			onBack();
		}, [checkUnsavedChanges, currentTabId, onBack]);
		const handleTabSelect = useCallback(
			(tab: string, title: string) => {
				if (checkUnsavedChanges()) return;
				onTabSelect(tab, title);
			},
			[checkUnsavedChanges, onTabSelect],
		);
		// Kept here rather than in the list so the query survives opening a result and coming back.
		const [searchQuery, setSearchQuery] = useState('');
		const [searchPendingSection, setSearchPendingSection] = useState<string | null>(null);
		const handleSearchResultSelect = useCallback(
			(tab: SettingsTab, section: string | null) => {
				if (checkUnsavedChanges()) return;
				setSearchPendingSection(section);
				onTabSelect(tab.type, tab.label);
			},
			[checkUnsavedChanges, onTabSelect],
		);
		const pendingScrollSection = pendingSection ?? searchPendingSection ?? initialPendingSection;
		const handlePendingSectionConsumed = useCallback(() => {
			if (pendingSection) {
				onPendingSectionConsumed?.();
			}
			if (searchPendingSection) {
				setSearchPendingSection(null);
			}
			if (initialPendingSection) {
				setInitialPendingSection(null);
			}
		}, [initialPendingSection, onPendingSectionConsumed, pendingSection, searchPendingSection]);
		const showMobileList = mobileNav.isRootView;
		const showMobileContent = !mobileNav.isRootView;
		const notificationsSubpage = currentTabId === 'notifications' ? PhoneNotificationsPage.page : null;
		const mobileContentTitle =
			currentTabId === 'applications' && ApplicationsTabState.isDetailView
				? (ApplicationsTabState.selectedApplication?.name ?? i18n._(APPLICATION_DETAILS_DESCRIPTOR))
				: notificationsSubpage
					? i18n._(PHONE_NOTIFICATIONS_SUBPAGE_TITLES[notificationsSubpage])
					: currentTab?.label || mobileNav.currentView?.title;
		const mobilePageLinkHref =
			currentTabId && !(currentTabId === 'applications' && ApplicationsTabState.isDetailView) && !notificationsSubpage
				? buildUserSettingsDeepLink(currentTabId)
				: null;
		const listScrollPositionRef = useRef(0);
		const listScrollerRef = useRef<ScrollerHandle | null>(null);
		const handleListScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
			listScrollPositionRef.current = event.currentTarget.scrollTop;
		}, []);
		useEffect(() => {
			if (!showMobileList) return;
			const scroller = listScrollerRef.current;
			if (!scroller) return;
			const target = listScrollPositionRef.current;
			if (target === 0) return;
			scroller.scrollTo({to: target, animate: false});
		}, [showMobileList]);
		const currentTabComponent = currentTab ? getSettingsTabComponent(currentTab.type) : null;
		return (
			<div className={userSettingsStyles.mobileWrapper} data-flx="app.mobile-settings-view.div">
				<div className={userSettingsStyles.mobileHeaderContainer} data-flx="app.mobile-settings-view.div--2">
					<AnimatePresence
						mode="wait"
						custom={mobileNav.direction}
						data-flx="app.mobile-settings-view.animate-presence"
					>
						{showMobileList && (
							<motion.div
								key="mobile-list-header"
								variants={headerFadeVariants}
								initial="center"
								animate="center"
								exit="exit"
								transition={{duration: 0.08, ease: 'easeInOut'}}
								className={userSettingsStyles.mobileHeaderContent}
								data-flx="app.mobile-settings-view.div--3"
							>
								<MobileHeader
									title={i18n._(SETTINGS_DESCRIPTOR)}
									onBack={handleBack}
									data-flx="app.mobile-settings-view.mobile-header"
								/>
							</motion.div>
						)}
						{showMobileContent && currentTab && (
							<motion.div
								key={`mobile-content-header-${mobileNav.currentView?.tab}`}
								variants={headerFadeVariants}
								initial="enter"
								animate="center"
								exit="exit"
								transition={{duration: 0.08, ease: 'easeInOut'}}
								className={userSettingsStyles.mobileHeaderContent}
								data-flx="app.mobile-settings-view.div--4"
							>
								<MobileHeaderWithBanner
									title={mobileContentTitle || currentTab.label}
									pageLinkHref={mobilePageLinkHref}
									onBack={handleBack}
									showUnsavedBanner={showUnsavedBanner}
									flashBanner={flashBanner}
									tabData={tabData}
									data-flx="app.mobile-settings-view.mobile-header-with-banner"
								/>
							</motion.div>
						)}
					</AnimatePresence>
				</div>
				<div className={userSettingsStyles.mobileContentContainer} data-flx="app.mobile-settings-view.div--5">
					<AnimatePresence
						mode="wait"
						custom={mobileNav.direction}
						data-flx="app.mobile-settings-view.animate-presence--2"
					>
						{showMobileList && (
							<motion.div
								key="mobile-list-content"
								custom={mobileNav.direction}
								variants={contentFadeVariants}
								initial="center"
								animate="center"
								exit="exit"
								transition={{duration: 0.15, ease: 'easeInOut'}}
								className={userSettingsStyles.mobileContentPane}
								data-flx="app.mobile-settings-view.div--6"
							>
								<MobileSettingsList
									groupedTabs={groupedSettingsTabs}
									onTabSelect={handleTabSelect}
									onSearchResultSelect={handleSearchResultSelect}
									searchQuery={searchQuery}
									onSearchQueryChange={setSearchQuery}
									scrollRef={listScrollerRef}
									onScroll={handleListScroll}
									data-flx="app.mobile-settings-view.mobile-settings-list"
								/>
							</motion.div>
						)}
						{showMobileContent && currentTab && (
							<motion.div
								key={`mobile-content-${mobileNav.currentView?.tab}`}
								custom={mobileNav.direction}
								variants={contentFadeVariants}
								initial="enter"
								animate="center"
								exit="exit"
								transition={{duration: 0.15, ease: 'easeInOut'}}
								className={userSettingsStyles.mobileContentPane}
								data-flx="app.mobile-settings-view.div--7"
							>
								<MobileContentWithScrollSpy
									scrollKey={scrollKey}
									settingsTabType={currentTab.type}
									initialGuildId={initialGuildId}
									initialSubtab={initialSubtab}
									currentTabComponent={currentTabComponent}
									pendingSection={pendingScrollSection}
									onPendingSectionConsumed={handlePendingSectionConsumed}
									data-flx="app.mobile-settings-view.mobile-content-with-scroll-spy"
								/>
							</motion.div>
						)}
					</AnimatePresence>
				</div>
			</div>
		);
	},
);
